/**
 * 华奋达「最后一次可信」虚拟键盘布局缓存。
 * 2.4G 脏包/半包时不要回退出厂矩阵，应回退到本缓存，保证各页显示真实改键。
 */

export type HfdTrustedLayoutRow = {
  layer: number;
  row: number;
  keyboardLayout: number[];
  keyboardData?: any[];
  ledBeads?: any;
};

function readEquipmentFingerprint(): {
  vendorId: number;
  productId: number;
  keyboardType: string;
} {
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("equipmentData")
        : null;
    if (!raw) {
      return { vendorId: 0, productId: 0, keyboardType: "" };
    }
    const eq = JSON.parse(raw) as Record<string, unknown>;
    return {
      vendorId: Number(eq.vendorId ?? 0),
      productId: Number(eq.productId ?? 0),
      keyboardType: String(eq.keyboardType ?? ""),
    };
  } catch {
    return { vendorId: 0, productId: 0, keyboardType: "" };
  }
}

export function getHfdTrustedLayoutStorageKey(
  keyboardType: string,
  layer = 0
): string {
  const fp = readEquipmentFingerprint();
  const kt = String(keyboardType || fp.keyboardType || "unknown");
  return `hfdTrustedLayoutRows:${fp.vendorId}:${fp.productId}:${kt}:layer${layer}`;
}

export function getHfdTrustedLayoutRows(
  keyboardType: string,
  layer = 0
): HfdTrustedLayoutRow[] | null {
  try {
    const raw = localStorage.getItem(
      getHfdTrustedLayoutStorageKey(keyboardType, layer)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed as HfdTrustedLayoutRow[];
  } catch {
    return null;
  }
}

export function setHfdTrustedLayoutRows(
  keyboardType: string,
  layer: number,
  rows: HfdTrustedLayoutRow[]
): void {
  if (!Array.isArray(rows) || rows.length === 0) return;
  try {
    localStorage.setItem(
      getHfdTrustedLayoutStorageKey(keyboardType, layer),
      JSON.stringify(
        rows.map((r) => ({
          layer: Number(r.layer ?? layer),
          row: Number(r.row ?? 0),
          keyboardLayout: Array.isArray(r.keyboardLayout)
            ? r.keyboardLayout.map((c) => Number(c ?? 0))
            : [],
        }))
      )
    );
  } catch {
    // ignore quota
  }
}

export function patchHfdTrustedLayoutCell(
  keyboardType: string,
  layer: number,
  row: number,
  col: number,
  keycode: number
): void {
  const rows = getHfdTrustedLayoutRows(keyboardType, layer);
  if (!rows?.length) return;
  const next = rows.map((item) => {
    if (Number(item.row) !== Number(row)) return item;
    const layout = [...(item.keyboardLayout ?? [])];
    if (col < 0 || col >= layout.length) return item;
    layout[col] = Number(keycode);
    return { ...item, keyboardLayout: layout };
  });
  setHfdTrustedLayoutRows(keyboardType, layer, next);
}

export function clearHfdTrustedLayoutRows(
  keyboardType: string,
  layer = 0
): void {
  try {
    localStorage.removeItem(getHfdTrustedLayoutStorageKey(keyboardType, layer));
  } catch {
    // ignore
  }
}
